import { AdgroupFormData, InterestTargetType } from '@tt4b/ttam-enums';
import { StateEnum, useStore } from '@tt4b/ttam-hooks';
import { Ref, ref, computed, watch } from '@vue/composition-api';
import { i18n } from './../../../../i18n';
import { trackEvent } from './../../../../utils/index';

import * as targetTypeHooks from './target-type';
import * as inMarketHooks from './in-market-category';
import * as commonInterestsHooks from './common-interests-category';
import * as offlineAlertHook from './offline-alert';
import * as trackHooks from './track';

import { cloneDeep } from 'lodash-es';
import { getIdLabelMap, walkTreeBFS, getIdItemMap } from '@tt4b/ttam-utils';

function useContainerProps(options: { adgroupData: Ref<AdgroupFormData>; targetType: Ref<InterestTargetType> }) {
  const { adgroupData, targetType } = options;

  const contentVisible = ref(false);

  const inMarketTags = computed(() => adgroupData.value?.inMarketTags || []);
  const adTagV2 = computed(() => adgroupData.value?.adTagV2 || []);

  const hasOperated = computed(() => Boolean(inMarketTags.value.length || adTagV2.value.length));
  const searchResultSelection = computed(() => [
    ...adTagV2.value,
    ...(adgroupData.value?.interestKeywordsI18n?.map?.(id => `${id}-keyword`) || []),
  ]);

  watch(searchResultSelection, (value, oldValue) => {
    if (value?.length > oldValue?.length) {
      trackEvent('create_campaign_targeting_interest_searchresult_click', {
        adgroup_id: adgroupData.value?.adId || '',
      });
    }
  });

  const selected = computed(() => searchResultSelection.value);

  /** 是否是默认的文案，只有收起且无操作时候才是默认 */
  const isDefaultText = computed(() => !(contentVisible.value || selected.value?.length));

  /** 标题 */
  const title = computed(() =>
    isDefaultText.value
      ? i18n.t('adgroup_target_interest_add_interests')
      : i18n.t('adgroup_target_interest_add_interests_title'),
  );

  /** 描述 */
  const desc = computed(() => {
    if (isDefaultText.value) {
      return i18n.t('module_adgroup_target_interest_desc');
    }

    return targetType.value === InterestTargetType.CommonInterest
      ? i18n.t('module_adgroup_target_interest_general_interests_selected', { num: selected.value?.length })
      : i18n.t('module_adgroup_target_interest_purchase_interests_selected', {
          num: inMarketTags.value.length,
        });
  });
  return {
    contentVisible,
    title,
    desc,
    hasOperated,
    selected,
  };
}

export function usePropsInterestName(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const adTagV2 = computed(() => adgroupData.value?.adTagV2 || []);
  const { state } = useStore();
  const interestList = computed(() => state[StateEnum.interestList].data.value);
  const interestCategoriesInfoMap = computed(() => {
    const map = {};
    walkTreeBFS({
      root: { children: cloneDeep(interestList.value) },
      handler: node => {
        map[node.id] = node;
        node.keyword_id = node.id;
        node.keyword = node.i18n_value;
      },
    });

    return map;
  });
  const interestOptions = computed(() => interestList.value.map(({ id }) => interestCategoriesInfoMap.value[id]));
  const interestMap = computed(() => getIdLabelMap(interestOptions.value, 'i18n_value'));
  const interestItemMap = computed(() => getIdItemMap(interestOptions.value));
  const interestName = computed(() => {
    if (!adTagV2.value.length) {
      return i18n.t('adgroup_no_limit') as string;
    }

    return adTagV2.value.map(item => interestMap.value?.[item]);
  });

  return {
    interestName,
    interestItemMap,
  };
}

export function usePropsInterestKeywordsName(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { state } = useStore();
  const interestKeywordsInfoMap = computed(() => state[StateEnum.targetInfo].value.interestKeywordsInfoMap);
  const interestKeywordsName = computed(() =>
    adgroupData.value.interestKeywordsI18n.map(id => interestKeywordsInfoMap.value[id]?.i18n_value ?? id),
  );

  return {
    interestKeywordsName,
  };
}

export default {
  ...targetTypeHooks,
  ...inMarketHooks,
  ...commonInterestsHooks,
  ...offlineAlertHook,
  ...trackHooks,
  useContainerProps,
  usePropsInterestKeywordsName,
  usePropsInterestName,
};
